import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2, MoveHorizontal } from "lucide-react";

type Props = {
  src: string;
  alt: string;
};

export function Panorama360Viewer({ src, alt }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawRef = useRef<(() => void) | null>(null);
  const viewRef = useRef({ yaw: 0, pitch: 0, fov: Math.PI / 2.25 });
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const [unsupported, setUnsupported] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext("webgl", { alpha: false, antialias: true });
    if (!gl) {
      setUnsupported(true);
      return;
    }
    setUnsupported(false);

    const compile = (type: number, source: string) => {
      const shader = gl.createShader(type);
      if (!shader) throw new Error("No fue posible crear el visor 360.");
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) || "No fue posible compilar el visor 360.");
      }
      return shader;
    };
    const vertex = compile(gl.VERTEX_SHADER, `
      attribute vec2 a_position;
      varying vec2 v_position;
      void main() {
        v_position = a_position;
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `);
    const fragment = compile(gl.FRAGMENT_SHADER, `
      precision highp float;
      varying vec2 v_position;
      uniform sampler2D u_texture;
      uniform float u_yaw;
      uniform float u_pitch;
      uniform float u_fov;
      uniform float u_aspect;
      void main() {
        float scale = tan(u_fov * 0.5);
        vec3 ray = normalize(vec3(v_position.x * u_aspect * scale, v_position.y * scale, 1.0));
        float cp = cos(u_pitch);
        float sp = sin(u_pitch);
        ray = vec3(ray.x, ray.y * cp - ray.z * sp, ray.y * sp + ray.z * cp);
        float cy = cos(u_yaw);
        float sy = sin(u_yaw);
        ray = vec3(ray.x * cy + ray.z * sy, ray.y, -ray.x * sy + ray.z * cy);
        float longitude = atan(ray.x, ray.z);
        float latitude = asin(clamp(ray.y, -1.0, 1.0));
        vec2 uv = vec2(fract(longitude / 6.28318530718 + 0.5), clamp(0.5 - latitude / 3.14159265359, 0.0, 1.0));
        gl_FragColor = texture2D(u_texture, uv);
      }
    `);
    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, "a_position");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([25, 55, 72, 255]));

    let imageReady = false;
    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const pixelRatio = Math.min(2, window.devicePixelRatio || 1);
      const width = Math.max(1, Math.round(rect.width * pixelRatio));
      const height = Math.max(1, Math.round(rect.height * pixelRatio));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      gl.viewport(0, 0, width, height);
      gl.useProgram(program);
      gl.uniform1f(gl.getUniformLocation(program, "u_yaw"), viewRef.current.yaw);
      gl.uniform1f(gl.getUniformLocation(program, "u_pitch"), viewRef.current.pitch);
      gl.uniform1f(gl.getUniformLocation(program, "u_fov"), viewRef.current.fov);
      gl.uniform1f(gl.getUniformLocation(program, "u_aspect"), width / height);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };
    drawRef.current = draw;
    const image = new Image();
    image.onload = () => {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      imageReady = true;
      draw();
    };
    image.src = src;
    const resizeObserver = new ResizeObserver(draw);
    resizeObserver.observe(canvas);
    draw();
    return () => {
      image.onload = null;
      resizeObserver.disconnect();
      drawRef.current = null;
      if (imageReady) gl.bindTexture(gl.TEXTURE_2D, null);
      gl.deleteTexture(texture);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
    };
  }, [src]);

  const rotate = (delta: number) => {
    viewRef.current.yaw += delta;
    drawRef.current?.();
  };

  return (
    <div
      className="panorama-viewer"
      ref={hostRef}
      tabIndex={0}
      aria-label={alt}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") rotate(-0.16);
        if (event.key === "ArrowRight") rotate(0.16);
      }}
      onPointerDown={(event) => {
        dragRef.current = { x: event.clientX, y: event.clientY };
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        const drag = dragRef.current;
        if (!drag) return;
        viewRef.current.yaw -= (event.clientX - drag.x) * 0.005;
        viewRef.current.pitch = Math.max(-1.25, Math.min(1.25, viewRef.current.pitch + (event.clientY - drag.y) * 0.004));
        dragRef.current = { x: event.clientX, y: event.clientY };
        drawRef.current?.();
      }}
      onPointerUp={() => { dragRef.current = null; }}
      onPointerCancel={() => { dragRef.current = null; }}
      onWheel={(event) => {
        viewRef.current.fov = Math.max(0.55, Math.min(1.75, viewRef.current.fov + event.deltaY * 0.001));
        drawRef.current?.();
      }}
    >
      {unsupported ? <img src={src} alt={alt} /> : <canvas ref={canvasRef} role="img" aria-label={alt} />}
      <span className="panorama-drag-hint"><MoveHorizontal />Arrastra para explorar</span>
      <span className="panorama-degree">360°</span>
      <button type="button" className="panorama-left" aria-label="Girar a la izquierda" onClick={() => rotate(-0.35)}><ChevronLeft /></button>
      <button type="button" className="panorama-right" aria-label="Girar a la derecha" onClick={() => rotate(0.35)}><ChevronRight /></button>
      <button type="button" className="panorama-fullscreen" aria-label="Abrir en pantalla completa" onClick={() => hostRef.current?.requestFullscreen?.()}><Maximize2 /></button>
    </div>
  );
}
